import { useEffect, useRef, useState } from 'react';

export default function CTASection() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
    }
  };

  return (
    <section id="cta" ref={ref} className="bg-charcoal-400 py-24 md:py-36 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-sage-600/10 rounded-full blur-[100px]" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className={`transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-300 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              Begin Your Practice
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-white font-medium leading-tight mb-6">
              Start practicing
              <br />
              <span className="italic font-light">financial stewardship.</span>
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed mb-10">
              Build peace, visibility, and intentionality with your money. Join the movement and be the first to experience our platform.
            </p>
          </div>

          {/* Form */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {!submitted ? (
              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d806qeu4oug4kst80vo0"
                method="POST"
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-white/10 border border-white/15 text-white placeholder-white/40 px-5 py-3.5 rounded-full text-sm focus:outline-none focus:border-sage-400/50 transition-colors"
                />
                <button
                  type="submit"
                  className="bg-sage-400 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-sage-500 transition-colors whitespace-nowrap"
                >
                  Join the Movement
                </button>
              </form>
            ) : (
              <div className="bg-sage-500/20 rounded-2xl px-8 py-6 max-w-md mx-auto">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-sage-400 flex items-center justify-center">
                    <i className="ri-check-line text-white" />
                  </div>
                  <p className="text-white font-medium text-lg">You are in.</p>
                </div>
                <p className="text-white/60 text-sm">
                  We will be in touch with practices, reflections, and early access.
                </p>
              </div>
            )}
            <p className="text-white/30 text-xs mt-4">
              No spam. Just gentle stewardship practices and reflections.
            </p>
          </div>

          {/* Secondary CTA */}
          <div
            className={`mt-12 transition-all duration-1000 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <button
              onClick={() => {
                const el = document.querySelector('#philosophy');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-white/50 text-sm hover:text-white/80 transition-colors"
            >
              Read the Philosophy first
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}